/**
 * Classifying the document-linkage delete pre-flight's outcome.
 *
 * ⚠️ THIS EXISTS BECAUSE EVERY FAILURE LOOKED THE SAME. The three delete surfaces call
 * `GET /deals/:dealId/documents/:documentId/linkage` before opening their destructive confirm and
 * are FAIL-CLOSED — a failed pre-flight blocks the delete. Both fetchers collapsed every failure to
 * `undefined`, so a 403 (the caller may not delete this document, ever) was reported with the same
 * "please try again" wording as a network blip, telling the user to retry something that can never
 * succeed. VE-2358 was one instance of that; the misreporting is the general case.
 *
 * ⚠️ The pre-flight fetchers do NOT reject on an HTTP error. `useAxios`'s response interceptor
 * RETURNS the error from its rejection handler (`return error`), which RESOLVES the promise with the
 * `AxiosError` — so `await axios.get(...)` yields the error object, `res.data` is `undefined`, and
 * the `catch` block never runs. That quirk is why the old code failed closed "by accident", and why
 * the status has to be read off the RESOLVED value here rather than from a caught exception.
 */

/** Why a linkage pre-flight could not be completed. */
export type DocumentLinkageFailureReason = 'forbidden' | 'unavailable';

/** Which delete the pre-flight was guarding — only the wording differs. */
export type LinkageDeleteSubject = 'document' | 'deliverable';

/** A completed pre-flight, or the reason it could not be completed. */
export type DocumentLinkageResult<T> = { ok: true; linkage: T } | { ok: false; reason: DocumentLinkageFailureReason };

/**
 * Wording for a pre-flight failure that a retry could plausibly fix (network, 5xx, 404).
 * Unchanged from before this module existed — it stays correct for everything but a denial.
 */
export const LINKAGE_UNAVAILABLE_MESSAGE = "Couldn't verify what this delete affects — please try again.";

/** Status that means the caller is not permitted to delete, so retrying is pointless. */
const HTTP_FORBIDDEN = 403;

/**
 * Narrows an unknown value to an indexable object without reaching for `any`.
 * @param {unknown} value - Candidate value.
 * @returns {boolean} True when the value can be read as a record.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * True for the `AxiosError` the interceptor hands back in place of a rejection.
 * @param {unknown} value - The value `await axios.get(...)` resolved with.
 * @returns {boolean} True when the value is an Axios error rather than a response.
 */
const isAxiosErrorLike = (value: unknown): value is { response?: { status?: number } } =>
  isRecord(value) && value.isAxiosError === true;

/**
 * Classifies what a linkage pre-flight resolved with into success or a typed failure.
 *
 * An `AxiosError` is ALWAYS a failure, even when it carries a body — a 403's body is an error
 * payload, never a linkage payload, and treating it as one would open an empty destructive confirm.
 * @template T - The linkage payload type the caller expects.
 * @param {unknown} value - Whatever the `axios.get` call resolved with (response OR AxiosError).
 * @returns {DocumentLinkageResult<T>} Success with the payload, or the failure reason.
 */
export function classifyLinkageResponse<T>(value: unknown): DocumentLinkageResult<T> {
  if (isAxiosErrorLike(value)) {
    return {
      ok: false,
      reason: value.response?.status === HTTP_FORBIDDEN ? 'forbidden' : 'unavailable'
    };
  }

  if (isRecord(value) && value.data != null) {
    return { ok: true, linkage: value.data as T };
  }

  return { ok: false, reason: 'unavailable' };
}

/**
 * The message a delete surface shows when its pre-flight failed. Defined once so the three
 * surfaces cannot drift into describing the same failure differently.
 * @param {DocumentLinkageFailureReason} reason - Why the pre-flight failed.
 * @param {LinkageDeleteSubject} subject - What the blocked delete was going to remove.
 * @returns {string} User-facing message.
 */
export function linkageFailureMessage(reason: DocumentLinkageFailureReason, subject: LinkageDeleteSubject): string {
  if (reason === 'forbidden') {
    return `You don't have permission to delete this ${subject}.`;
  }
  return LINKAGE_UNAVAILABLE_MESSAGE;
}
